import {
  Popover,
  type PopoverContentProps,
  type PopoverPortalProps,
  type PopoverRootOptions,
} from "@kobalte/core/popover";
import { createPresence } from "@solid-primitives/presence";
import { createSignal, type JSX, Show, type Signal, splitProps } from "solid-js";
import { twMerge } from "tailwind-merge";
import { AnimateChildren, menuScale } from "../animation/AnimateChildren";

export const StPopover = (
  props: {
    noArrow?: boolean;
    children: JSX.Element;
    content: JSX.Element;
    portalProps?: Omit<PopoverPortalProps, "children">;
    contentProps?: PopoverContentProps & JSX.HTMLAttributes<HTMLDivElement>;
  } & PopoverRootOptions
) => {
  const signal = props.onOpenChange ? ([() => props.open, props.onOpenChange] as Signal<boolean>) : createSignal(false);
  const [open, setOpen] = signal;

  const duration = 100;
  const presence = createPresence(() => props.open || open(), {
    transitionDuration: duration,
  });

  const [split, rest] = splitProps(props, ["content", "portalProps", "contentProps", "noArrow"]);
  return (
    <Popover {...rest} onOpenChange={setOpen} open={open()} forceMount={presence.isMounted()}>
      {rest.children}

      <Popover.Portal {...split.portalProps}>
        <AnimateChildren
          show={open()}
          duration={duration}
          presence={presence}
          enter={{
            scale: [menuScale(), 1],
            opacity: [0, 1],
          }}
          exit={{
            scale: menuScale(),
            opacity: 0,
          }}
        >
          {(anim) => (
            <Popover.Content
              {...split.contentProps}
              ref={anim.ref}
              class={twMerge(
                `z-20 origin-(--kb-popover-content-transform-origin) rounded-lg border border-primary-border bg-tile px-2 py-1 text-sm text-primary-text shadow-xl`,
                split.contentProps?.class
              )}
            >
              <Show when={!split.noArrow}>
                <Popover.Arrow />
              </Show>

              {split.content}
            </Popover.Content>
          )}
        </AnimateChildren>
      </Popover.Portal>
    </Popover>
  );
};

export const StPopoverTrigger = Popover.Trigger;
